/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo, useState, useEffect} from 'react'
import {Helmet} from 'react-helmet'

import {useCategory, useProduct} from '@salesforce/commerce-sdk-react'
import {useParams} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useMultiSite from '../../hooks/use-multi-site'
import {FormattedMessage, useIntl} from 'react-intl'
import {CARD_ARIA_LABEL} from '../../constants'

// Constants
import ImageGallery from '../../components/image-gallery'
import {
    Box,
    useMultiStyleConfig,
    Skeleton,
    VStack,
    Heading,
    Flex,
    Text,
    Button,
    useDisclosure,
    useBreakpointValue,
    Divider,
    VisuallyHidden
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Breadcrumb from '../../components/breadcrumb'
import {Skeleton as ImageGallerySkeleton} from '@salesforce/retail-react-app/app/components/image-gallery'
import DisplayPrice from '../../components/display-price'
import Link from '@salesforce/retail-react-app/app/components/link'
import ProductSetDrawer from './product-set-drawer'
import DynamicImage from '@app/components/dynamic-image'
import useShipToCountry from '../../hooks/use-ship-to-country'
import PageNotFound from '../page-not-found'

// Custom hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * The `ProductSet` component is responsible for rendering a detailed view of a product set,
 * including its images, description, and individual products within the set. It utilizes
 * various hooks and components to fetch data, manage state, and handle UI interactions.
 *
 * The component fetches the product set data using the `useProduct` hook, and if the product
 * set is not found (404 error), it redirects the user to the home page. It also fetches
 * category data for breadcrumb navigation using the `useCategory` hook.
 *
 * The component displays a gallery of images for the product set and lists each product
 * within the set, providing options to view more details or open a drawer with additional
 * product information. The drawer is managed using the `useDisclosure` hook.
 *
 * The component also includes SEO considerations by setting the `robots` meta tag to
 * "noindex" using the `Helmet` component.
 *
 * Hooks and utilities used:
 * - `useParams`: To extract the product set ID from the URL.
 * - `useServerContext`: To access server-side context data.
 * - `useMultiSite`: To build URLs for navigation.
 * - `useMultiStyleConfig`: To apply consistent styling across components.
 * - `useDisclosure`: To manage the open/close state of the product drawer.
 * - `useBreakpointValue`: To determine the placement of the drawer based on screen size.
 * - `useState`: To manage the state of the selected product.
 * - `useMemo`: To memoize the filtered list of products for performance optimization.
 *
 * @returns {JSX.Element} The rendered component displaying the product set details.
 */
const ProductSet = () => {
    const {formatMessage} = useIntl()
    const {shipToCountry} = useShipToCountry()
    const {productSetId} = useParams()
    const {res} = useServerContext()
    const styles = useMultiStyleConfig('ProductViewContainer')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const placement = useBreakpointValue({base: 'bottom', md: 'right'})
    const [selectedProduct, setSelectedProduct] = useState(null)
    const {usePageLoadAndCookies, triggerViewItemEvent} = useGTM()

    const {
        data: productSetData,
        isError: isProductSetError,
        error: productSetError
    } = useProduct({
        parameters: {
            id: productSetId.toUpperCase(), // API is case-sensitive, IDs in BM are uppercase
            allImages: true,
            perPricebook: true,
            c_country: shipToCountry?.id
        }
    })

    const {data: category, isLoading: isCategoryDataLoading} = useCategory({
        parameters: {
            id: productSetData?.primaryCategoryId,
            levels: 1
        },
        enabled: productSetData?.primaryCategoryId
    })

    // Use useMemo to memoize the filtered product data
    const filteredProducts = useMemo(() => {
        return productSetData?.setProducts?.map((product) => {
            const frontImage = product.imageGroups?.find((group) => group.viewType === 'front')
                ?.images?.[0]?.link
            return {...product, frontImage}
        })
    }, [productSetData])

    usePageLoadAndCookies('product')

    /*
        Redirect to home page if product set is not online
    */
    if (isProductSetError && productSetError?.response?.status === 404) {
        return <PageNotFound />
    }

    /**
     * Opens the product drawer with the details of the selected product.
     *
     * @param {Object} product - The product object containing the details of the product.
     * @param {string} frontImage - The URL of the product's front image.
     */
    const handleOpenDrawer = (product, frontImage) => {
        setSelectedProduct({...product, frontImage})
        onOpen()
    }

    /**
     * Handles the click event on a link within the product set page.
     *
     * This function triggers a 'select_item' event using the `triggerViewItemEvent` function,
     * passing the `productSetData` as an argument. It is typically used to track user interactions
     * with individual items in a product set, allowing for analytics and user behavior insights.
     *
     * @function handleLinkClick
     */
    const handleLinkClick = () => {
        triggerViewItemEvent('select_item', productSetData)
    }

    return (
        <>
            <Helmet>
                <meta name="robots" content="noindex" />
            </Helmet>

            {/* Breadcrumb at the top of the page */}
            {category?.c_extendedParentCategoryTree && (
                <Skeleton isLoaded={!isCategoryDataLoading} minWidth={64}>
                    <Breadcrumb
                        categories={category?.c_extendedParentCategoryTree}
                        product={productSetData}
                        type="productBreadcrumb"
                    />
                </Skeleton>
            )}
            {/* End Breadcrumb at the top of the page */}
            <Flex
                direction={'column'}
                data-testid="product-view"
                pt={{base: '16px', md: 'initial'}}
            >
                <Flex
                    direction={['column', 'column', 'column', 'row']}
                    {...styles.productViewLayout}
                >
                    <Box {...styles.productImages}>
                        {productSetData ? (
                            <ImageGallery
                                product={productSetData}
                                size={'md'}
                                imageGroups={productSetData?.imageGroups}
                                isProductSets={true}
                            />
                        ) : (
                            <ImageGallerySkeleton />
                        )}
                    </Box>

                    <Box {...styles.productDescription} alignSelf="flex-start" height={'initial'}>
                        <VStack {...styles.productAttributes}>
                            <Box display={'block'}>
                                <VStack {...styles.productViewHeaderContainer}>
                                    <Skeleton
                                        isLoaded={productSetData?.name}
                                        {...styles.productBrandHeader}
                                    >
                                        <Heading
                                            as="h1"
                                            width={'341px'}
                                            fontSize={'4xl'}
                                            marginBottom={'10px'}
                                        >{`${productSetData?.name}`}</Heading>
                                    </Skeleton>
                                </VStack>

                                <Text textTransform={'uppercase'} fontSize={'md'} pt={'30px'}>
                                    <FormattedMessage
                                        id="product_set.text.product_set_includes"
                                        defaultMessage="product set includes:"
                                    />
                                </Text>
                                {filteredProducts?.map((product, index) => {
                                    const {id, name, price, currency, c_color, imageGroups} =
                                        product
                                    /**
                                     * Retrieves the base link of the "front" image for a product that matches the specified color.
                                     *
                                     * This function searches through the image groups of a product to find the group
                                     * where the `viewType` is "front" and the color matches `product.c_color`.
                                     * If such a group is found, it returns the base link of the first image in that group.
                                     *
                                     * @param {Array} imageGroups - An array of image groups associated with the product.
                                     * Each group contains information about the images and their variation attributes.
                                     * @param {Object} product - The product object containing details of the product, including
                                     * the current color (`c_color`).
                                     * @returns {string|null} The base link of the "front" image that matches the product's color,
                                     * or `null` if no matching image is found.
                                     */

                                    const frontImage = imageGroups?.find((group) => {
                                        const colorValue = group.variationAttributes?.find(
                                            (attr) => attr.id === 'color'
                                        )?.values?.[0]?.value
                                        return group.viewType === 'front' && colorValue === c_color
                                    })?.images?.[0]?.disBaseLink
                                    return (
                                        <>
                                            <Flex
                                                key={id}
                                                mt={4}
                                                align="center"
                                                pt={'20px'}
                                                role="group"
                                                aria-label={formatMessage(CARD_ARIA_LABEL.PRODUCT)}
                                            >
                                                {frontImage && (
                                                    <Box>
                                                        <DynamicImage
                                                            width={'114px'}
                                                            src={`${frontImage}[?sw={width}]`}
                                                            widths={[
                                                                '384px',
                                                                '384px',
                                                                '400px',
                                                                '360px',
                                                                '480px',
                                                                '505px'
                                                            ]}
                                                            imageProps={{
                                                                alt: name,
                                                                loading: 'lazy'
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                                <Box pl={'30px'}>
                                                    <Text fontFamily={'Anton'} width={'207px'}>
                                                        {name}
                                                    </Text>
                                                    <DisplayPrice
                                                        pt={'8px'}
                                                        priceData={{
                                                            currentPrice: price
                                                        }}
                                                        currency={currency}
                                                    />
                                                    <Button
                                                        mt={'20px'}
                                                        variant="primary"
                                                        width={'207px'}
                                                        onClick={() =>
                                                            handleOpenDrawer(product, frontImage)
                                                        }
                                                    >
                                                        <FormattedMessage
                                                            id="product_set.button.view_options"
                                                            defaultMessage="View Options"
                                                        />
                                                    </Button>
                                                    <Box
                                                        textAlign={'center'}
                                                        fontSize={'md'}
                                                        pt={'20px'}
                                                    >
                                                        <Link
                                                            onClick={handleLinkClick}
                                                            to={`/product/${id}`}
                                                        >
                                                            <FormattedMessage
                                                                id="product_set.link.view_product_details"
                                                                defaultMessage="View Product Details"
                                                            />
                                                        </Link>
                                                    </Box>
                                                </Box>
                                            </Flex>
                                            {index < filteredProducts?.length - 1 && (
                                                <Divider
                                                    pt={'20px'}
                                                    orientation="horizontal"
                                                    borderColor={'borderGray'}
                                                />
                                            )}
                                        </>
                                    )
                                })}
                            </Box>
                        </VStack>
                    </Box>
                </Flex>
            </Flex>

            <ProductSetDrawer
                isOpen={isOpen}
                onClose={onClose}
                placement={placement}
                productSetData={selectedProduct || {}}
                frontImage={selectedProduct?.frontImage}
            />
        </>
    )
}

ProductSet.getTemplateName = () => 'product-set'

export default ProductSet
